import React, { useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Download, Palette, Eye, Sparkles, User, MapPin, Briefcase,
  Heart, Crown, FileText, Check, ChevronDown, ChevronUp, GripVertical
} from "lucide-react";
import { toast } from "sonner";

// ── Palette options ──────────────────────────────────────────────────────────
const THEMES = [
  { id: "rose",    name: "Rose Gold",   primary: "#C2185B", accent: "#FCE4EC", header: "#880E4F", text: "#4a1942" },
  { id: "emerald", name: "Emerald",     primary: "#00796B", accent: "#E0F2F1", header: "#004D40", text: "#1b3a34" },
  { id: "royal",   name: "Royal Blue",  primary: "#1565C0", accent: "#E3F2FD", header: "#0D47A1", text: "#0d2461" },
  { id: "saffron", name: "Saffron",     primary: "#E65100", accent: "#FFF3E0", header: "#BF360C", text: "#5d2006" },
  { id: "violet",  name: "Violet",      primary: "#6A1B9A", accent: "#F3E5F5", header: "#4A148C", text: "#2e0b4c" },
  { id: "slate",   name: "Classic Grey",primary: "#37474F", accent: "#ECEFF1", header: "#263238", text: "#1c2c33" },
];

const DEFAULT_SECTIONS = [
  { id: "personal",  label: "Personal Details",   enabled: true,  locked: true },
  { id: "contact",   label: "Contact Information", enabled: true,  locked: false },
  { id: "location",  label: "Location",            enabled: true,  locked: false },
  { id: "education", label: "Education & Career",  enabled: true,  locked: false },
  { id: "family",    label: "Family Details",      enabled: true,  locked: false },
  { id: "horoscope", label: "Horoscope / Kundli",  enabled: true,  locked: false },
  { id: "partner",   label: "Partner Preferences", enabled: true,  locked: false },
  { id: "about",     label: "About Me",            enabled: true,  locked: false },
];

function InfoRow({ label, value, color }) {
  if (!value && value !== 0) return null;
  return (
    <tr>
      <td style={{ padding: "5px 10px 5px 0", color: color, fontWeight: 600, whiteSpace: "nowrap", width: "38%", verticalAlign: "top", fontSize: 13 }}>
        {label}
      </td>
      <td style={{ padding: "5px 0", color: "#333", fontSize: 13, verticalAlign: "top" }}>
        : &nbsp;{value}
      </td>
    </tr>
  );
}

function SectionHeading({ title, theme }) {
  return (
    <div style={{
      background: `linear-gradient(90deg, ${theme.primary}, ${theme.header})`,
      color: "#fff", padding: "6px 14px", marginTop: 16, marginBottom: 8,
      borderRadius: 4, fontWeight: 700, fontSize: 13, letterSpacing: "0.06em",
      textTransform: "uppercase"
    }}>
      {title}
    </div>
  );
}

function BiodataPreview({ profile, theme, sections, customNote, printTitle }) {
  const enabledIds = new Set(sections.filter(s => s.enabled).map(s => s.id));

  const age = profile?.dob
    ? Math.floor((Date.now() - new Date(profile.dob)) / (365.25 * 24 * 3600 * 1000))
    : profile?.age;

  // Map of section id → rendered rows — rendered in the ORDER of sections array (drag-and-drop)
  const sectionContent = {
    personal: enabledIds.has("personal") ? (
      <>
        <tr><td colSpan={2}><SectionHeading title="Personal Details" theme={theme} /></td></tr>
        <InfoRow label="Full Name"          value={profile?.name}                                           color={theme.primary} />
        <InfoRow label="Date of Birth"      value={profile?.dob}                                           color={theme.primary} />
        <InfoRow label="Age"                value={age ? `${age} Years` : null}                           color={theme.primary} />
        <InfoRow label="Height"             value={profile?.height_cm ? `${profile.height_cm} cm` : null} color={theme.primary} />
        <InfoRow label="Marital Status"     value={profile?.marital_status}                                color={theme.primary} />
        <InfoRow label="Religion"           value={profile?.religion}                                      color={theme.primary} />
        <InfoRow label="Caste"              value={profile?.caste}                                         color={theme.primary} />
        <InfoRow label="Mother Tongue"      value={profile?.mother_tongue}                                 color={theme.primary} />
        <InfoRow label="Diet"               value={profile?.diet}                                          color={theme.primary} />
        <InfoRow label="Complexion"         value={profile?.complexion}                                    color={theme.primary} />
      </>
    ) : null,

    contact: enabledIds.has("contact") ? (
      <>
        <tr><td colSpan={2}><SectionHeading title="Contact Information" theme={theme} /></td></tr>
        <InfoRow label="Phone" value={profile?.phone} color={theme.primary} />
        <InfoRow label="Email" value={profile?.email} color={theme.primary} />
      </>
    ) : null,

    location: enabledIds.has("location") ? (
      <>
        <tr><td colSpan={2}><SectionHeading title="Location" theme={theme} /></td></tr>
        <InfoRow label="Village/Town" value={profile?.village}  color={theme.primary} />
        <InfoRow label="Block"        value={profile?.block}    color={theme.primary} />
        <InfoRow label="District"     value={profile?.district} color={theme.primary} />
        <InfoRow label="State"        value="Jharkhand"         color={theme.primary} />
      </>
    ) : null,

    education: enabledIds.has("education") ? (
      <>
        <tr><td colSpan={2}><SectionHeading title="Education & Career" theme={theme} /></td></tr>
        <InfoRow label="Education"     value={profile?.education} color={theme.primary} />
        <InfoRow label="Occupation"    value={profile?.occupation} color={theme.primary} />
        <InfoRow label="Annual Income" value={profile?.income}    color={theme.primary} />
      </>
    ) : null,

    family: enabledIds.has("family") ? (
      <>
        <tr><td colSpan={2}><SectionHeading title="Family Details" theme={theme} /></td></tr>
        <InfoRow label="Family Type"         value={profile?.family_type}    color={theme.primary} />
        <InfoRow label="Family Status"       value={profile?.family_status}  color={theme.primary} />
        <InfoRow label="Father's Name"       value={profile?.father_name}    color={theme.primary} />
        <InfoRow label="Father's Occupation" value={profile?.father_occupation} color={theme.primary} />
        <InfoRow label="Mother's Name"       value={profile?.mother_name}    color={theme.primary} />
        <InfoRow label="Siblings"            value={profile?.siblings != null ? profile.siblings : null} color={theme.primary} />
      </>
    ) : null,

    horoscope: enabledIds.has("horoscope") ? (
      <>
        <tr><td colSpan={2}><SectionHeading title="Horoscope / Kundli" theme={theme} /></td></tr>
        <InfoRow label="Rashi"       value={profile?.rashi}       color={theme.primary} />
        <InfoRow label="Nakshatra"   value={profile?.nakshatra}   color={theme.primary} />
        <InfoRow label="Manglik"     value={profile?.manglik}     color={theme.primary} />
        <InfoRow label="Birth Time"  value={profile?.birth_time}  color={theme.primary} />
        <InfoRow label="Birth Place" value={profile?.birth_place} color={theme.primary} />
      </>
    ) : null,

    partner: (enabledIds.has("partner") && profile?.partner_pref) ? (
      <>
        <tr><td colSpan={2}><SectionHeading title="Partner Preferences" theme={theme} /></td></tr>
        <InfoRow label="Age Range"    value={`${profile.partner_pref.min_age || 18} – ${profile.partner_pref.max_age || 50} yrs`}         color={theme.primary} />
        <InfoRow label="Religion"     value={profile.partner_pref.religion  !== "Any" ? profile.partner_pref.religion  : "Open to All"}   color={theme.primary} />
        <InfoRow label="Education"    value={profile.partner_pref.education !== "Any" ? profile.partner_pref.education : "Open to All"}   color={theme.primary} />
        <InfoRow label="Manglik Pref" value={profile.partner_pref.manglik   !== "Any" ? profile.partner_pref.manglik   : "No Preference"} color={theme.primary} />
      </>
    ) : null,

    about: (enabledIds.has("about") && profile?.about) ? (
      <>
        <tr><td colSpan={2}><SectionHeading title="About Me" theme={theme} /></td></tr>
        <tr>
          <td colSpan={2} style={{ padding: "6px 0", fontSize: 13, color: "#444", lineHeight: 1.6 }}>
            {profile.about}
          </td>
        </tr>
      </>
    ) : null,
  };

  return (
    <div id="biodata-print-area" style={{
      fontFamily: "'Georgia', serif", background: "#fff",
      padding: "28px 28px", maxWidth: 700, margin: "0 auto",
      border: `2px solid ${theme.primary}`, borderRadius: 8,
      boxShadow: "0 4px 32px rgba(0,0,0,0.08)"
    }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${theme.header} 0%, ${theme.primary} 100%)`,
        padding: "18px 22px", borderRadius: 6, marginBottom: 20,
        display: "flex", alignItems: "center", gap: 18
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", letterSpacing: "0.15em", textTransform: "uppercase" }}>
            {printTitle || "Matrimonial Bio-Data"}
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: "#fff", lineHeight: 1.15, marginTop: 3 }}>
            {profile?.name || "Your Name"}
          </div>
          <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, marginTop: 4 }}>
            {[age ? `${age} yrs` : "", profile?.height_cm ? `${profile.height_cm} cm` : "", profile?.marital_status].filter(Boolean).join(" · ")}
          </div>
        </div>
        {/* Decorative OM */}
        <div style={{ fontSize: 40, color: "rgba(255,255,255,0.15)", userSelect: "none", marginRight: 4 }}>ॐ</div>
      </div>

      {/* Sections rendered in drag-and-drop order */}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {sections.map(sec => {
            if (!sec.enabled) return null;
            if (sec.id === "personal") return (
              <React.Fragment key="personal">
                <tr><td colSpan={2}><SectionHeading title="Personal Details" theme={theme} /></td></tr>
                <InfoRow label="Full Name"      value={profile?.name} color={theme.primary} />
                <InfoRow label="Date of Birth"  value={profile?.dob} color={theme.primary} />
                <InfoRow label="Age"            value={age ? `${age} Years` : null} color={theme.primary} />
                <InfoRow label="Height"         value={profile?.height_cm ? `${profile.height_cm} cm` : null} color={theme.primary} />
                <InfoRow label="Marital Status" value={profile?.marital_status} color={theme.primary} />
                <InfoRow label="Religion"       value={profile?.religion} color={theme.primary} />
                <InfoRow label="Caste"          value={profile?.caste} color={theme.primary} />
                <InfoRow label="Mother Tongue"  value={profile?.mother_tongue} color={theme.primary} />
                <InfoRow label="Diet"           value={profile?.diet} color={theme.primary} />
                <InfoRow label="Complexion"     value={profile?.complexion} color={theme.primary} />
              </React.Fragment>
            );
            if (sec.id === "contact") return (
              <React.Fragment key="contact">
                <tr><td colSpan={2}><SectionHeading title="Contact Information" theme={theme} /></td></tr>
                <InfoRow label="Phone" value={profile?.phone} color={theme.primary} />
                <InfoRow label="Email" value={profile?.email} color={theme.primary} />
              </React.Fragment>
            );
            if (sec.id === "location") return (
              <React.Fragment key="location">
                <tr><td colSpan={2}><SectionHeading title="Location" theme={theme} /></td></tr>
                <InfoRow label="Village/Town" value={profile?.village}  color={theme.primary} />
                <InfoRow label="Block"        value={profile?.block}    color={theme.primary} />
                <InfoRow label="District"     value={profile?.district} color={theme.primary} />
                <InfoRow label="State"        value="Jharkhand"         color={theme.primary} />
              </React.Fragment>
            );
            if (sec.id === "education") return (
              <React.Fragment key="education">
                <tr><td colSpan={2}><SectionHeading title="Education & Career" theme={theme} /></td></tr>
                <InfoRow label="Education"     value={profile?.education}  color={theme.primary} />
                <InfoRow label="Occupation"    value={profile?.occupation} color={theme.primary} />
                <InfoRow label="Annual Income" value={profile?.income}     color={theme.primary} />
              </React.Fragment>
            );
            if (sec.id === "family") return (
              <React.Fragment key="family">
                <tr><td colSpan={2}><SectionHeading title="Family Details" theme={theme} /></td></tr>
                <InfoRow label="Family Type"         value={profile?.family_type}      color={theme.primary} />
                <InfoRow label="Family Status"       value={profile?.family_status}    color={theme.primary} />
                <InfoRow label="Father's Name"       value={profile?.father_name}      color={theme.primary} />
                <InfoRow label="Father's Occupation" value={profile?.father_occupation} color={theme.primary} />
                <InfoRow label="Mother's Name"       value={profile?.mother_name}      color={theme.primary} />
                <InfoRow label="Siblings"            value={profile?.siblings != null ? profile.siblings : null} color={theme.primary} />
              </React.Fragment>
            );
            if (sec.id === "horoscope") return (
              <React.Fragment key="horoscope">
                <tr><td colSpan={2}><SectionHeading title="Horoscope / Kundli" theme={theme} /></td></tr>
                <InfoRow label="Rashi"       value={profile?.rashi}       color={theme.primary} />
                <InfoRow label="Nakshatra"   value={profile?.nakshatra}   color={theme.primary} />
                <InfoRow label="Manglik"     value={profile?.manglik}     color={theme.primary} />
                <InfoRow label="Birth Time"  value={profile?.birth_time}  color={theme.primary} />
                <InfoRow label="Birth Place" value={profile?.birth_place} color={theme.primary} />
              </React.Fragment>
            );
            if (sec.id === "partner" && profile?.partner_pref) return (
              <React.Fragment key="partner">
                <tr><td colSpan={2}><SectionHeading title="Partner Preferences" theme={theme} /></td></tr>
                <InfoRow label="Age Range"    value={`${profile.partner_pref.min_age||18} – ${profile.partner_pref.max_age||50} yrs`} color={theme.primary} />
                <InfoRow label="Religion"     value={profile.partner_pref.religion  !== "Any" ? profile.partner_pref.religion  : "Open to All"} color={theme.primary} />
                <InfoRow label="Education"    value={profile.partner_pref.education !== "Any" ? profile.partner_pref.education : "Open to All"} color={theme.primary} />
                <InfoRow label="Manglik Pref" value={profile.partner_pref.manglik   !== "Any" ? profile.partner_pref.manglik   : "No Preference"} color={theme.primary} />
              </React.Fragment>
            );
            if (sec.id === "about" && profile?.about) return (
              <React.Fragment key="about">
                <tr><td colSpan={2}><SectionHeading title="About Me" theme={theme} /></td></tr>
                <tr><td colSpan={2} style={{ padding: "6px 0", fontSize: 13, color: "#444", lineHeight: 1.6 }}>{profile.about}</td></tr>
              </React.Fragment>
            );
            return null;
          })}


        </tbody>
      </table>

      {/* Custom Note */}
      {customNote && (
        <div style={{
          marginTop: 18, padding: "10px 14px", background: theme.accent,
          borderLeft: `4px solid ${theme.primary}`, borderRadius: 4,
          fontSize: 12, color: "#555", fontStyle: "italic"
        }}>
          {customNote}
        </div>
      )}

      {/* Footer */}
      <div style={{
        marginTop: 18, borderTop: `2px solid ${theme.primary}`,
        paddingTop: 10, textAlign: "center",
        color: theme.primary, fontSize: 11, letterSpacing: "0.08em"
      }}>
        Created with ❤ on hamarsathi.com
      </div>
    </div>
  );
}

export default function BiodataBuilder() {
  const auth = useAuth();
  const user = auth?.user || null;

  // Guest sample data so the tool is usable without login
  const GUEST_SAMPLE = {
    name: "Your Name",
    dob: "",
    height_cm: "",
    marital_status: "Never Married",
    religion: "Hindu",
    caste: "",
    mother_tongue: "Hindi",
    diet: "Vegetarian",
    district: "Ranchi",
    village: "",
    block: "",
    education: "",
    occupation: "",
    income: "",
    about: "",
    rashi: "",
    nakshatra: "",
    manglik: "",
    birth_time: "",
    birth_place: "",
    phone: "",
    email: "",
    partner_pref: { min_age: 22, max_age: 35, religion: "Any", education: "Any", manglik: "Any" },
  };

  const profile = user || GUEST_SAMPLE;

  const [theme, setTheme] = useState(THEMES[0]);
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [customNote, setCustomNote] = useState("");
  const [printTitle, setPrintTitle] = useState("Matrimonial Bio-Data");
  const [showPreview, setShowPreview] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const printAreaRef = useRef(null);


  const handleDownload = async () => {
    setDownloading(true);
    try {
      const printContents = document.getElementById("biodata-print-area")?.innerHTML;
      if (!printContents) { toast.error("Preview not ready"); return; }
      const win = window.open("", "_blank", "width=900,height=700");
      win.document.write(`
        <!DOCTYPE html><html><head>
        <title>${printTitle}</title>
        <style>
          body { margin: 0; padding: 20px; background: #fff; font-family: Georgia, serif; }
          @media print { body { padding: 0; } }
          img { max-width: 100%; }
        </style></head>
        <body>${printContents}</body></html>
      `);
      win.document.close();
      win.focus();
      setTimeout(() => { win.print(); setDownloading(false); }, 500);
    } catch {
      toast.error("Could not open print dialog");
      setDownloading(false);
    }
  };

  // ── Section toggle ────────────────────────────────────────────────────────
  const toggleSection = (id) => {
    setSections(prev => prev.map(s =>
      s.id === id && !s.locked ? { ...s, enabled: !s.enabled } : s
    ));
  };

  // ── Drag-and-drop reorder ─────────────────────────────────────────────────
  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setSections(prev => {
      const next = [...prev];
      const [removed] = next.splice(dragIdx, 1);
      next.splice(idx, 0, removed);
      setDragIdx(idx);
      return next;
    });
  };
  const onDragEnd = () => setDragIdx(null);

  return (
    <div className="container-px py-8 max-w-7xl mx-auto">
      {/* Page Title */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="label-cap">Free Feature — No Login Required</span>
          <h1 className="font-heading text-4xl text-primary mt-1 flex items-center gap-2">
            <FileText className="w-8 h-8 text-secondary" />
            Bio-Data Builder
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
            Build a beautiful matrimonial bio-data — customise theme, reorder sections and download as PDF. Free for everyone!
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="rounded-full gap-2"
            onClick={() => setShowPreview(v => !v)}
            data-testid="biodata-toggle-preview"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button>
          <Button
            className="rounded-full gap-2 bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold shadow"
            onClick={handleDownload}
            disabled={downloading}
            data-testid="biodata-download-btn"
          >
            <Download className="w-4 h-4" />
            {downloading ? "Preparing…" : "Download / Print"}
          </Button>
        </div>
      </div>

      {/* Guest banner */}
      {!user && (
        <div className="mb-6 flex items-center gap-3 rounded-2xl border border-secondary/30 bg-secondary/5 px-5 py-3.5">
          <FileText className="w-5 h-5 text-secondary shrink-0" />
          <p className="text-sm text-foreground flex-1">
            <span className="font-semibold text-secondary">Guest Mode:</span> You can explore and print the bio-data tool right now!{" "}
            <Link to="/login" className="underline text-primary font-medium hover:text-primary/80">Login</Link> or{" "}
            <Link to="/register" className="underline text-primary font-medium hover:text-primary/80">Register free</Link> to auto-fill with your profile details.
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-[320px_1fr] gap-8 items-start">

        {/* ── Left Panel: Customiser ────────────────────────────────────────── */}
        <aside className="space-y-5">

          {/* Theme Picker */}
          <div className="card-soft p-5">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="w-4 h-4 text-secondary" />
              <span className="font-semibold text-sm text-foreground">Colour Theme</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {THEMES.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTheme(t)}
                  title={t.name}
                  data-testid={`theme-${t.id}`}
                  className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all ${
                    theme.id === t.id
                      ? "border-primary shadow-md scale-105"
                      : "border-transparent hover:border-muted-foreground/30"
                  }`}
                >
                  <div className="w-8 h-8 rounded-full shadow" style={{ background: t.primary }} />
                  <span className="text-[10px] text-muted-foreground leading-tight text-center">{t.name}</span>
                  {theme.id === t.id && <Check className="w-3 h-3 text-primary" strokeWidth={3} />}
                </button>
              ))}
            </div>
          </div>

          {/* Title Field */}
          <div className="card-soft p-5">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-secondary" />
              <span className="font-semibold text-sm">Document Title</span>
            </div>
            <Input
              value={printTitle}
              onChange={e => setPrintTitle(e.target.value)}
              placeholder="e.g. Matrimonial Bio-Data"
              data-testid="biodata-title-input"
            />
          </div>

          {/* Section Manager */}
          <div className="card-soft p-5">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-secondary" />
              <span className="font-semibold text-sm">Sections &amp; Order</span>
              <span className="ml-auto text-[10px] text-muted-foreground">Drag to reorder</span>
            </div>
            <ul className="space-y-2">
              {sections.map((sec, idx) => (
                <li
                  key={sec.id}
                  draggable={!sec.locked}
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={e => onDragOver(e, idx)}
                  onDragEnd={onDragEnd}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${
                    dragIdx === idx ? "border-primary/50 bg-primary/5" : "border-border bg-muted/20"
                  } ${sec.locked ? "opacity-80" : "cursor-grab active:cursor-grabbing"}`}
                  data-testid={`section-row-${sec.id}`}
                >
                  <GripVertical className={`w-4 h-4 shrink-0 ${sec.locked ? "text-muted-foreground/30" : "text-muted-foreground"}`} />
                  <span className="flex-1 text-sm font-medium">{sec.label}</span>
                  {sec.locked ? (
                    <span className="text-[10px] text-muted-foreground px-2 py-0.5 bg-muted rounded-full">Required</span>
                  ) : (
                    <button
                      onClick={() => toggleSection(sec.id)}
                      data-testid={`toggle-${sec.id}`}
                      className={`w-9 h-5 rounded-full transition-colors relative shrink-0 ${
                        sec.enabled ? "bg-primary" : "bg-muted-foreground/30"
                      }`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        sec.enabled ? "translate-x-4" : "translate-x-0.5"
                      }`} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Custom Note */}
          <div className="card-soft p-5">
            <div className="flex items-center gap-2 mb-3">
              <Heart className="w-4 h-4 text-secondary" />
              <span className="font-semibold text-sm">Custom Note (Optional)</span>
            </div>
            <Textarea
              value={customNote}
              onChange={e => setCustomNote(e.target.value)}
              placeholder="Add a personal message, family note, or astrological remark…"
              rows={3}
              data-testid="biodata-custom-note"
            />
          </div>

          {/* Tips */}
          <div className="card-soft p-4 bg-secondary/5 border border-secondary/20">
            <p className="text-xs text-secondary font-semibold mb-1.5">💡 Tips</p>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-3">
              <li>Fill your profile details to show more info in the bio-data.</li>
              <li>Add a profile photo for a professional look.</li>
              <li>Use <strong>Download / Print</strong> to save as PDF from your browser.</li>
              <li>Drag sections to reorder them on the document.</li>
            </ul>
          </div>
        </aside>

        {/* ── Right Panel: Live Preview ─────────────────────────────────────── */}
        {showPreview && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-4 h-4 text-secondary" />
              <span className="font-semibold text-sm text-foreground">Live Preview</span>
              <span className="text-[10px] text-muted-foreground ml-2">Updates in real-time</span>
            </div>
            <div className="overflow-auto rounded-2xl shadow-xl border border-border">
              <BiodataPreview
                profile={profile}
                theme={theme}
                sections={sections}
                customNote={customNote}
                printTitle={printTitle}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
